import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useInvoicePermissions } from '../../hooks/useInvoicePermissions';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  ImportExport as ExportIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  SaveAlt as SaveIcon,
  Cancel as CancelIcon,
  FileDownload as FileDownloadIcon,
  MoreVert as MoreVertIcon,
  Receipt as ReceiptIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Error as ErrorIcon,
  HelpOutline as HelpOutlineIcon,
  AccountBalance as AccountBalanceIcon,
  Warning as WarningIcon,
  Payment as PaymentIcon,
  TableChart as TableChartIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import * as invoiceService from '../../services/invoiceService';
import * as printTemplateService from '../../services/printTemplateService';
import * as financeService from '../../services/financeService';
import { IInvoice, IInvoiceCreate, IInvoicePage, IInvoiceItem } from '../../types/invoice';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import InvoicePaymentManagementDialog from './InvoicePaymentManagementDialog';

// Interface for invoice event status
interface InvoiceEventStatus {
  status: 'success' | 'failed' | 'in_progress' | 'no_events' | 'unknown';
  hasErrors: boolean;
  totalEvents?: number;
  failedEvents?: number;
  successEvents?: number;
}

// مكون الدائرة لحالة الأحداث
interface EventStatusIndicatorProps {
  status: InvoiceEventStatus | undefined;
  loading: boolean;
  invoiceId: string;
}

const EventStatusIndicator: React.FC<EventStatusIndicatorProps> = ({ status, loading, invoiceId }) => {
  if (loading) {
    return (
      <Tooltip title="جاري التحقق من حالة الأحداث...">
        <CircularProgress size={20} />
      </Tooltip>
    );
  }

  if (!status || status.status === 'no_events' || status.status === 'unknown') {
    return (
      <Tooltip title="لا توجد أحداث مسجلة لهذه الفاتورة">
        <HelpOutlineIcon sx={{ color: 'grey.400', fontSize: 24 }} />
      </Tooltip>
    );
  }

  if (status.status === 'success') {
    return (
      <Tooltip title={`جميع الأحداث نجحت (${status.successEvents || 0} حدث)`}>
        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
      </Tooltip>
    );
  }

  if (status.status === 'failed' || status.hasErrors) {
    return (
      <Tooltip
        title={`يوجد أخطاء في الأحداث (${status.failedEvents || 0} فاشل من ${status.totalEvents || 0})`}
      >
        <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />
      </Tooltip>
    );
  }

  if (status.status === 'in_progress') {
    return (
      <Tooltip title="الفاتورة قيد التنفيذ">
        <CircularProgress size={20} sx={{ color: 'warning.main' }} />
      </Tooltip>
    );
  }

  return null;
};

// مكون المؤشر لحالة المعاملات المالية (مع التحقق المتقدم)
interface TransactionStatusIndicatorProps {
  transactionStatus: {
    hasTransactions: boolean;
    count: number;
    totalDebit: number;
    totalCredit: number;
    transactionTypes: string[];
    transactionTypeCounts?: Record<string, number>; // إضافة عدد كل نوع
    verification: {
      status: 'complete' | 'partial' | 'missing' | 'no_transactions';
      invoiceTotal: number;
      paymentTransactionsTotal: number;
      amountMatch: boolean;
      customerSupplierRequired: boolean;
      customerSupplierExists: boolean;
      customerSupplierMatch: boolean;
      paymentTransactionsCount: number;
      customerSupplierTransactionsCount: number;
    };
  } | undefined;
  loading: boolean;
  invoiceNumber: string;
  invoice?: IInvoice; // إضافة الفاتورة للحصول على عدد القيود
}

const TransactionStatusIndicator: React.FC<TransactionStatusIndicatorProps> = ({
  transactionStatus,
  loading,
  invoiceNumber,
  invoice
}) => {
  if (loading) {
    return (
      <Tooltip title="جاري التحقق من المعاملات المالية...">
        <CircularProgress size={20} />
      </Tooltip>
    );
  }

  if (!transactionStatus || !transactionStatus.hasTransactions) {
    return (
      <Tooltip title="لا توجد معاملات مالية مسجلة لهذه الفاتورة">
        <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />
      </Tooltip>
    );
  }

  const verification = transactionStatus.verification;
  
  // بناء محتوى Tooltip المفصل
  const buildTooltipContent = () => {
    const lines: string[] = [];
    
    // معلومات المبالغ
    lines.push(`📊 المبالغ:`);
    lines.push(`   إجمالي الفاتورة: ${verification.invoiceTotal.toFixed(2)} ريال`);
    lines.push(`   معاملات الدفع: ${verification.paymentTransactionsTotal.toFixed(2)} ريال`);
    lines.push(`   ${verification.amountMatch ? '✅ متطابقة' : '❌ غير متطابقة'}`);
    
    lines.push('');
    
    // معلومات معاملة العميل/المورد
    if (verification.customerSupplierRequired) {
      lines.push(`👤 معاملة العميل/المورد:`);
      lines.push(`   الحالة: مطلوبة`);
      lines.push(`   ${verification.customerSupplierExists ? '✅ موجودة' : '❌ ناقصة'}`);
    } else {
      lines.push(`👤 معاملة العميل/المورد: غير مطلوبة`);
    }
    
    lines.push('');
    lines.push(`📝 إجمالي المعاملات: ${transactionStatus.count}`);
    
    // قسم منفصل للقيود التلقائية (في الأسفل)
    lines.push('');
    lines.push(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    lines.push('');
    
    // معلومات القيود التلقائية
    const entriesCount = invoice?.autoEntriesCount ?? 0;
    lines.push(`🔢 القيود التلقائية: ${entriesCount}`);
    
    // حساب عدد المعاملات المرتبطة بالقيود التلقائية من transactionTypeCounts
    if (transactionStatus.transactionTypeCounts) {
      const autoEntryTransactionsCount = transactionStatus.transactionTypeCounts['قيد فاتورة تلقائي'] || 0;
      lines.push(`💼 معاملات القيود التلقائية: ${autoEntryTransactionsCount}`);
    }
    
    return lines.join('\n');
  };

  // تحديد الأيقونة واللون حسب الحالة
  let icon: React.ReactNode;
  let color: string;
  
  // التحقق من تطابق عدد القيود مع عدد المعاملات
  const entriesCount = invoice?.autoEntriesCount ?? 0;
  let autoEntryTransactionsCount = 0;
  
  if (transactionStatus.transactionTypeCounts) {
    autoEntryTransactionsCount = transactionStatus.transactionTypeCounts['قيد فاتورة تلقائي'] || 0;
  }
  
  const entriesMismatch = entriesCount > 0 && entriesCount !== autoEntryTransactionsCount;
  
  // إذا كان هناك عدم تطابق في القيود، اجعل الحالة warning
  if (entriesMismatch) {
    icon = <WarningIcon sx={{ color: 'warning.main', fontSize: 24 }} />;
    color = 'warning.main';
  } else {
    switch (verification.status) {
      case 'complete':
        icon = <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />;
        color = 'success.main';
        break;
      case 'partial':
        icon = <WarningIcon sx={{ color: 'warning.main', fontSize: 24 }} />;
        color = 'warning.main';
        break;
      case 'missing':
        icon = <ErrorIcon sx={{ color: 'error.main', fontSize: 24 }} />;
        color = 'error.main';
        break;
      default:
        icon = <HelpOutlineIcon sx={{ color: 'grey.400', fontSize: 24 }} />;
        color = 'grey.400';
    }
  }

  return (
    <Tooltip
      title={
        <Box sx={{ whiteSpace: 'pre-line', fontSize: '0.875rem' }}>
          {buildTooltipContent()}
        </Box>
      }
      arrow
    >
      {icon}
    </Tooltip>
  );
};

const InvoicePage: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  // استخدام hook الصلاحيات للتحقق من صلاحيات المستخدم
  const { hasInvoicePagePermission, hasInvoiceSettingPermission } = useInvoicePermissions();
  
  const [page, setPage] = useState<IInvoicePage | null>(null);
  const [invoices, setInvoices] = useState<IInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: ''
  });
  const [printTemplatesDialog, setPrintTemplatesDialog] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('');
  const [printTemplates, setPrintTemplates] = useState<{id: string, name: string}[]>([]);
  
  // حالات نافذة التسديد
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState<IInvoice | null>(null);
  
  // حالات الأحداث للفواتير (للدوائر الخضراء/الحمراء)
  const [invoiceStatuses, setInvoiceStatuses] = useState<Record<string, InvoiceEventStatus>>({});
  const [loadingStatuses, setLoadingStatuses] = useState<boolean>(false);

  // حالات المعاملات المالية للفواتير
  const [invoiceTransactions, setInvoiceTransactions] = useState<Record<string, {
    hasTransactions: boolean;
    count: number;
    totalDebit: number;
    totalCredit: number;
    transactionTypes: string[];
    verification: {
      status: 'complete' | 'partial' | 'missing' | 'no_transactions';
      invoiceTotal: number;
      paymentTransactionsTotal: number;
      amountMatch: boolean;
      customerSupplierRequired: boolean;
      customerSupplierExists: boolean;
      customerSupplierMatch: boolean;
      paymentTransactionsCount: number;
      customerSupplierTransactionsCount: number;
    };
  }>>({});
  const [loadingTransactions, setLoadingTransactions] = useState<boolean>(false);

  // حالات إجمالي الدفعات للفواتير
  const [invoicePaymentTotals, setInvoicePaymentTotals] = useState<Record<string, number>>({});
  const [loadingPaymentTotals, setLoadingPaymentTotals] = useState<boolean>(false);

  // حالة تصدير Excel
  const [exportingExcel, setExportingExcel] = useState<boolean>(false);

  // جلب حالات الأحداث للفواتير المعروضة
  useEffect(() => {
    const fetchInvoiceStatuses = async () => {
      if (invoices.length === 0) return;
      
      setLoadingStatuses(true);
      try {
        // جمع أرقام الفواتير (referenceNumber) وليس معرفات الفواتير
        const invoiceNumbers = invoices
          .map(inv => inv.invoiceNumber)
          .filter(Boolean);
        
        if (invoiceNumbers.length === 0) {
          setLoadingStatuses(false);
          return;
        }

        // طلب حالات جميع الفواتير دفعة واحدة باستخدام أرقام الفواتير مع السياق
        const { apiWithContext } = await import('../../services/api');
        const response = await apiWithContext.post(
          '/inventory-events/transfer-status/bulk',
          { referenceNumbers: invoiceNumbers },
          'invoice'
        );

        if (response.data.success) {
          // تحويل النتائج من invoiceNumber إلى invoice._id للعرض
          const statusesByInvoiceId: Record<string, InvoiceEventStatus> = {};
          invoices.forEach(invoice => {
            if (invoice.invoiceNumber && response.data.statuses[invoice.invoiceNumber]) {
              statusesByInvoiceId[invoice._id!] = response.data.statuses[invoice.invoiceNumber];
            }
          });
          setInvoiceStatuses(statusesByInvoiceId);
        }
      } catch (error) {
        console.error('خطأ في جلب حالات الفواتير:', error);
        // لا نعرض خطأ للمستخدم لأن هذه ميزة إضافية
      } finally {
        setLoadingStatuses(false);
      }
    };

    // تأخير بسيط لتجنب الطلبات المتزامنة وعدم التأثير على سرعة التحميل
    const timer = setTimeout(() => {
      fetchInvoiceStatuses();
    }, 500);

    return () => clearTimeout(timer);
  }, [invoices]);

  // جلب حالات المعاملات المالية للفواتير المعروضة
  useEffect(() => {
    const fetchInvoiceTransactions = async () => {
      if (invoices.length === 0) return;
      
      setLoadingTransactions(true);
      try {
        // جمع أرقام الفواتير
        const invoiceNumbers = invoices
          .map(inv => inv.invoiceNumber)
          .filter(Boolean);
        
        if (invoiceNumbers.length === 0) {
          setLoadingTransactions(false);
          return;
        }

        // طلب حالات المعاملات المالية لجميع الفواتير دفعة واحدة
        const response = await financeService.checkInvoicesHaveTransactions(invoiceNumbers);

        if (response.success) {
          // تحويل النتائج من invoiceNumber إلى invoice._id للعرض
          const transactionsByInvoiceId: Record<string, any> = {};
          invoices.forEach(invoice => {
            if (invoice.invoiceNumber && response.data[invoice.invoiceNumber]) {
              transactionsByInvoiceId[invoice._id!] = response.data[invoice.invoiceNumber];
            }
          });
          setInvoiceTransactions(transactionsByInvoiceId);
        }
      } catch (error) {
        console.error('خطأ في جلب حالات المعاملات المالية:', error);
        // لا نعرض خطأ للمستخدم لأن هذه ميزة إضافية
      } finally {
        setLoadingTransactions(false);
      }
    };

    // تأخير بسيط لتجنب الطلبات المتزامنة
    const timer = setTimeout(() => {
      fetchInvoiceTransactions();
    }, 600);

    return () => clearTimeout(timer);
  }, [invoices]);

  // جلب إجمالي الدفعات للفواتير المعروضة
  useEffect(() => {
    const fetchInvoicePaymentTotals = async () => {
      if (invoices.length === 0 || !page?.enablePartialPayment) return;
      
      setLoadingPaymentTotals(true);
      try {
        // استيراد الخدمات
        const { getInvoicePayments, calculateTotalPayments } = await import('../../services/invoicePaymentManagementService');
        
        // جلب إجمالي الدفعات لكل فاتورة
        const totals: Record<string, number> = {};
        
        await Promise.all(
          invoices.map(async (invoice) => {
            try {
              console.log(`🔍 جلب الدفعات للفاتورة ${invoice.invoiceNumber} (ID: ${invoice._id})`);
              // جلب الدفعات للفاتورة
              const payments = await getInvoicePayments(invoice._id!);
              console.log(`📦 الدفعات المسترجعة للفاتورة ${invoice.invoiceNumber}:`, payments);
              console.log(`📊 عدد الدفعات: ${payments.length}`);
              
              // حساب إجمالي الدفعات من جدول الدفعات
              const paymentsTotal = calculateTotalPayments(payments);
              console.log(`💰 إجمالي الدفعات من الجدول: ${paymentsTotal}`);
              
              // إضافة المبلغ النقدي المدفوع مسبقاً
              const cashPaid = Number(invoice.cashPaidAmount || 0);
              console.log(`💵 المبلغ النقدي المدفوع مسبقاً: ${cashPaid}`);
              
              // إضافة إجمالي طرق الدفع
              const paymentMethodsTotal = invoice.paymentMethods && invoice.paymentMethods.length > 0
                ? invoice.paymentMethods.reduce((sum, pm) => sum + pm.amount, 0)
                : 0;
              console.log(`💳 إجمالي طرق الدفع: ${paymentMethodsTotal}`);
              
              // الإجمالي الكلي = دفعات الجدول + الدفعة النقدية المسبقة + طرق الدفع
              const totalPaid = paymentsTotal + cashPaid + paymentMethodsTotal;
              console.log(`💎 إجمالي المدفوع الكلي: ${totalPaid}`);
              console.log(`📈 إجمالي الفاتورة: ${invoice.total}`);
              console.log(`🔢 المتبقي المحسوب: ${invoice.total - totalPaid}`);
              
              totals[invoice._id!] = totalPaid;
            } catch (error) {
              console.error(`❌ خطأ في جلب إجمالي الدفعات للفاتورة ${invoice.invoiceNumber}:`, error);
              // في حالة الخطأ، استخدم cashPaidAmount + paymentMethods من الفاتورة
              const cashPaid = invoice.cashPaidAmount || 0;
              const paymentMethodsTotal = invoice.paymentMethods && invoice.paymentMethods.length > 0
                ? invoice.paymentMethods.reduce((sum, pm) => sum + pm.amount, 0)
                : 0;
              totals[invoice._id!] = cashPaid + paymentMethodsTotal;
            }
          })
        );
        
        console.log('✅ جميع إجماليات الدفعات:', totals);
        
        setInvoicePaymentTotals(totals);
      } catch (error) {
        console.error('خطأ في جلب إجمالي الدفعات:', error);
      } finally {
        setLoadingPaymentTotals(false);
      }
    };

    // تأخير بسيط لتجنب الطلبات المتزامنة
    const timer = setTimeout(() => {
      fetchInvoicePaymentTotals();
    }, 700);

    return () => clearTimeout(timer);
  }, [invoices, page?.enablePartialPayment]);

  // Fetch invoice page data and invoices
  useEffect(() => {
    const fetchData = async () => {
      if (!pageId) return;
      
      try {
        setLoading(true);
        
        console.log(`[InvoicePage] جلب تفاصيل الصفحة بمعرف: ${pageId}`);
        
        // Fetch page details
        const pageData = await invoiceService.getInvoicePageById(pageId);
        console.log(`[InvoicePage] تم جلب الصفحة بنجاح. المعرف التسلسلي: ${pageData.sequentialId}`);
        setPage(pageData);
        
        // Fetch invoices with filters
        const result = await invoiceService.getInvoices(
          pageId,
          statusFilter || undefined,
          dateRange.startDate || undefined,
          dateRange.endDate || undefined,
          undefined,
          undefined,
          currentPage,
          10,
          searchTerm || undefined
        );
        
        console.log('API response invoices:', result.invoices);
        
        // Debug first invoice if available
        if (result.invoices.length > 0) {
          console.log('First invoice data:', result.invoices[0]);
          console.log('First invoice branchId:', result.invoices[0].branchId);
          console.log('First invoice branchName:', result.invoices[0].branchName);
          console.log('First invoice createdByName:', result.invoices[0].createdByName);
        }
        
        setInvoices(result.invoices);
        setTotalPages(result.pagination.pages);
        setError(null);
      } catch (err) {
        console.error('Error fetching invoice data:', err);
        setError('حدث خطأ أثناء تحميل البيانات. يرجى المحاولة مرة أخرى.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [pageId, currentPage, statusFilter, dateRange, searchTerm]);

  // Handle page change in pagination
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  // Handle status filter change
  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  // Handle date filter change
  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when changing filter
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setDateRange({ startDate: '', endDate: '' });
    setCurrentPage(1);
  };

  // تصدير جميع الفواتير إلى Excel (كل الصفحات)
  const handleExportAllToExcel = useCallback(async () => {
    if (!pageId) return;
    // التحقق من صلاحية استيراد/تصدير كل الفواتير بالإكسل
    if (!hasInvoicePagePermission(page?.sequentialId || '', 'import_all_excel')) {
      alert('ليس لديك صلاحية لتصدير جميع الفواتير إلى Excel');
      return;
    }
    setExportingExcel(true);
    try {
      // جلب كل الفواتير بدون تحديد صفحة (limit كبير)
      const result = await invoiceService.getInvoices(
        pageId,
        statusFilter || undefined,
        dateRange.startDate || undefined,
        dateRange.endDate || undefined,
        undefined,
        undefined,
        1,
        10000, // جلب كل الفواتير
        searchTerm || undefined
      );

      const allInvoices = result.invoices;

      // إعداد بيانات الـ Excel
      const enablePartialPayment = page?.enablePartialPayment || false;

      // جلب إجماليات الدفعات إذا كان الدفع الجزئي مفعلاً
      let paymentTotalsMap: Record<string, number> = {};
      if (enablePartialPayment && allInvoices.length > 0) {
        try {
          const { getInvoicePayments, calculateTotalPayments } = await import('../../services/invoicePaymentManagementService');
          await Promise.all(
            allInvoices.map(async (invoice) => {
              try {
                const payments = await getInvoicePayments(invoice._id!);
                const paymentsTotal = calculateTotalPayments(payments);
                const cashPaid = Number(invoice.cashPaidAmount || 0);
                const paymentMethodsTotal = invoice.paymentMethods && invoice.paymentMethods.length > 0
                  ? invoice.paymentMethods.reduce((sum, pm) => sum + pm.amount, 0)
                  : 0;
                paymentTotalsMap[invoice._id!] = paymentsTotal + cashPaid + paymentMethodsTotal;
              } catch {
                const cashPaid = invoice.cashPaidAmount || 0;
                const paymentMethodsTotal = invoice.paymentMethods && invoice.paymentMethods.length > 0
                  ? invoice.paymentMethods.reduce((sum, pm) => sum + pm.amount, 0)
                  : 0;
                paymentTotalsMap[invoice._id!] = cashPaid + paymentMethodsTotal;
              }
            })
          );
        } catch (err) {
          console.error('خطأ في جلب إجماليات الدفعات للتصدير:', err);
        }
      }

      // بناء صفوف Excel
      const rows = allInvoices.map((invoice) => {
        const dateStr = invoice.date ? invoice.date.split('T')[0] : '';
        const [year, month, day] = dateStr.split('-');
        const formattedDate = dateStr ? `${day}/${month}/${year}` : '';

        const statusLabel = invoice.status === 'completed' ? 'مكتملة' : 'مسودة';

        const row: Record<string, any> = {
          'رقم الفاتورة': invoice.invoiceNumber || '',
          'التاريخ': formattedDate,
          'العميل / المورد': invoice.customerName || invoice.supplierName || '',
          'الفرع': invoice.branchName || '',
          'المستخدم': invoice.createdByName || '',
          'المجموع': invoice.total || 0,
          'الحالة': statusLabel,
        };

        if (enablePartialPayment) {
          const paid = paymentTotalsMap[invoice._id!] ?? 0;
          const remaining = (invoice.total || 0) - paid;
          row['المدفوع'] = paid;
          row['المتبقي'] = remaining;
        }

        return row;
      });

      // إنشاء ورقة Excel
      const worksheet = XLSX.utils.json_to_sheet(rows, { skipHeader: false });

      // تعيين عرض الأعمدة
      const colWidths = [
        { wch: 18 }, // رقم الفاتورة
        { wch: 14 }, // التاريخ
        { wch: 25 }, // العميل / المورد
        { wch: 18 }, // الفرع
        { wch: 18 }, // المستخدم
        { wch: 14 }, // المجموع
        { wch: 12 }, // الحالة
      ];
      if (enablePartialPayment) {
        colWidths.push({ wch: 14 }); // المدفوع
        colWidths.push({ wch: 14 }); // المتبقي
      }
      worksheet['!cols'] = colWidths;

      const workbook = XLSX.utils.book_new();
      const sheetName = page?.name ? page.name.substring(0, 31) : 'الفواتير';
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      // اسم الملف
      const fileName = `${page?.name || 'فواتير'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error('خطأ في تصدير Excel:', err);
      alert('حدث خطأ أثناء تصدير البيانات إلى Excel');
    } finally {
      setExportingExcel(false);
    }
  }, [pageId, statusFilter, dateRange, searchTerm, page, hasInvoicePagePermission]);

  // Handle search term change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Create a new invoice
  const handleCreateInvoice = () => {
    // التحقق من صلاحية إنشاء فاتورة جديدة
    if (!hasInvoicePagePermission(page?.sequentialId || '', 'create')) {
      alert('ليس لديك صلاحية لإنشاء فاتورة جديدة');
      return;
    }
    // Navigate to invoice creation page with pageId
    navigate(`/invoices/${pageId}/new`);
  };

  // Edit an existing invoice
  const handleEditInvoice = (invoiceId: string) => {
    // التحقق من صلاحية تعديل الفاتورة
    if (!hasInvoicePagePermission(page?.sequentialId || '', 'edit')) {
      alert('ليس لديك صلاحية لتعديل الفاتورة');
      return;
    }
    navigate(`/invoices/${pageId}/${invoiceId}/edit`);
  };

  // Delete an invoice
  const handleDeleteInvoice = async (invoiceId: string) => {
    // التحقق من صلاحية حذف الفاتورة
    if (!hasInvoicePagePermission(page?.sequentialId || '', 'delete')) {
      alert('ليس لديك صلاحية لحذف الفاتورة');
      return;
    }
    
    if (window.confirm('هل أنت متأكد من حذف هذه الفاتورة؟')) {
      try {
        await invoiceService.deleteInvoice(invoiceId);
        
        // Refresh invoices list
        setInvoices(invoices.filter(inv => inv._id !== invoiceId));
      } catch (err) {
        console.error('Error deleting invoice:', err);
        setError('حدث خطأ أثناء حذف الفاتورة. يرجى المحاولة مرة أخرى.');
      }
    }
  };

  // Print invoice using print templates
  const handlePrintInvoice = async (invoiceId: string) => {
    // التحقق من صلاحية طباعة الفاتورة
    if (!hasInvoicePagePermission(page?.sequentialId || '', 'print')) {
      alert('ليس لديك صلاحية لطباعة الفاتورة');
      return;
    }
    
    try {
      setSelectedInvoiceId(invoiceId);
      
      // الحصول على بيانات الصفحة للتحقق من وجود قوالب طباعة
      const pageData = await invoiceService.getInvoicePageById(pageId!);
      
      if (pageData.printTemplates && pageData.printTemplates.length > 0) {
        console.log('قوالب الطباعة المتاحة:', pageData.printTemplates);
        
        try {
          // جلب جميع قوالب الطباعة دفعة واحدة للحصول على أسمائها
          const allTemplates = await printTemplateService.getPrintTemplates(pageData.type);
          console.log('تم جلب قوالب الطباعة:', allTemplates);
          
          // تصفية القوالب حسب المعرفات المتاحة في الصفحة
          const templatesWithNames = pageData.printTemplates.map(templateId => {
            const template = allTemplates.find(t => t._id === templateId);
            return {
              id: templateId,
              name: template ? template.name : templateId // استخدم المعرف كاسم إذا لم يُعثر على القالب
            };
          });
          
          console.log('قوالب الطباعة مع أسمائها:', templatesWithNames);
          setPrintTemplates(templatesWithNames);
        } catch (error) {
          console.error('خطأ في جلب أسماء قوالب الطباعة:', error);
          // في حالة فشل جلب الأسماء، استخدم المعرفات كأسماء
          setPrintTemplates(pageData.printTemplates.map(id => ({ id, name: id })));
        }
        
        // فتح حوار اختيار قوالب الطباعة
        setPrintTemplatesDialog(true);
      } else if (pageData.defaultPrintTemplate) {
        // If there's a default template but no other templates, use the default
        window.open(`/api/invoices/${invoiceId}/print?templateId=${pageData.defaultPrintTemplate}`, '_blank');
      } else {
        // If no templates are available, fall back to default print
        invoiceService.printInvoice(invoiceId);
      }
    } catch (err) {
      console.error('Error preparing print templates:', err);
      // Fallback to default print in case of error
      invoiceService.printInvoice(invoiceId);
    }
  };

  // Export invoice to PDF
  const handleExportToPdf = (invoiceId: string) => {
    // التحقق من صلاحية تصدير الفاتورة إلى PDF
    if (!hasInvoicePagePermission(page?.sequentialId || '', 'print')) {
      alert('ليس لديك صلاحية لتصدير الفاتورة إلى PDF');
      return;
    }
    invoiceService.exportInvoiceToPdf(invoiceId);
  };

  // Export invoice to Excel
  const handleExportToExcel = (invoiceId: string) => {
    // التحقق من صلاحية تصدير الفاتورة إلى Excel
    if (!hasInvoicePagePermission(page?.sequentialId || '', 'import_excel')) {
      alert('ليس لديك صلاحية لتصدير الفاتورة إلى Excel');
      return;
    }
    invoiceService.exportInvoiceToExcel(invoiceId);
  };

  // Handle printing with selected template
  const handlePrintWithTemplate = (templateId: string) => {
    if (!selectedInvoiceId) return;
    
    // Open a new window with the print endpoint and the selected template
    window.open(`/api/invoices/${selectedInvoiceId}/print?templateId=${templateId}`, '_blank');
    
    // Close the dialog
    setPrintTemplatesDialog(false);
  };

  // View invoice details
  const handleViewInvoice = (invoiceId: string) => {
    // التحقق من صلاحية عرض تفاصيل الفاتورة
    if (!hasInvoicePagePermission(page?.sequentialId || '', 'view')) {
      alert('ليس لديك صلاحية لعرض تفاصيل الفاتورة');
      return;
    }
    navigate(`/invoices/${pageId}/${invoiceId}/view`);
  };

  // فتح نافذة تسديد الفاتورة
  const handleOpenPaymentDialog = (invoice: IInvoice) => {
    console.log('🔍 فتح نافذة التسديد للفاتورة:', invoice.invoiceNumber);
    console.log('💰 cashPaidAmount من الفاتورة:', invoice.cashPaidAmount);
    console.log('📋 بيانات الفاتورة الكاملة:', invoice);
    setSelectedInvoiceForPayment(invoice);
    setPaymentDialogOpen(true);
  };

  // إغلاق نافذة التسديد
  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setSelectedInvoiceForPayment(null);
    // إعادة تحميل البيانات لتحديث الجدول
    const fetchData = async () => {
      if (!pageId) return;
      
      try {
        const result = await invoiceService.getInvoices(
          pageId,
          statusFilter || undefined,
          dateRange.startDate || undefined,
          dateRange.endDate || undefined,
          undefined,
          undefined,
          currentPage,
          10,
          searchTerm || undefined
        );
        
        setInvoices(result.invoices);
        setTotalPages(result.pagination.pages);
      } catch (err) {
        console.error('Error refreshing invoice data:', err);
      }
    };
    
    fetchData();
  };

  // Go back to invoice settings
  const handleBackToSettings = () => {
    navigate('/invoices/settings');
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount);
  };

  // Render loading state
  if (loading && !page) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  // Render error state
  if (error && !page) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  // Render content
  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {/* Header */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box display="flex" alignItems="center">
            <ReceiptIcon color="primary" sx={{ mr: 1, flexShrink: 0 }} />
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {page?.name}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 1,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            {page?.type === 'sales_invoice' && 'فواتير المبيعات'}
            {page?.type === 'sales_return' && 'مرتجع مبيعات'}
            {page?.type === 'purchase_invoice' && 'فواتير المشتريات'}
            {page?.type === 'purchase_return' && 'مرتجع مشتريات'}
            {page?.type === 'quotation' && 'عروض الأسعار'}
            {page?.type === 'beginning_inventory' && 'بضاعة أول المدة'}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            flexShrink: 0
          }}
        >
          {/* عرض زر الإعدادات فقط إذا كان المستخدم يملك صلاحية الوصول لإعدادات الفواتير */}
          {hasInvoiceSettingPermission('settings', 'view_page') && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleBackToSettings}
              size="small"
              sx={{
                whiteSpace: 'nowrap',
                minWidth: 'auto',
                px: { xs: 2, sm: 3 }
              }}
            >
              الإعدادات
            </Button>
          )}
          {/* عرض زر إنشاء فاتورة فقط إذا كان المستخدم يملك الصلاحية */}
          {hasInvoicePagePermission(page?.sequentialId || '', 'create') && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateInvoice}
              size="small"
              sx={{
                whiteSpace: 'nowrap',
                minWidth: 'auto',
                px: { xs: 2, sm: 3 }
              }}
            >
              إنشاء فاتورة
            </Button>
          )}
        </Box>
      </Paper>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="بحث (رقم الفاتورة أو اسم العميل/المورد)"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                endAdornment: (
                  <SearchIcon color="action" />
                )
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>حالة الفاتورة</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusChange}
                label="حالة الفاتورة"
              >
                <MenuItem value="">الكل</MenuItem>
                <MenuItem value="draft">مسودة</MenuItem>
                <MenuItem value="completed">مكتملة</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="من تاريخ"
              type="date"
              value={dateRange.startDate}
              onChange={(e) => handleDateChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="إلى تاريخ"
              type="date"
              value={dateRange.endDate}
              onChange={(e) => handleDateChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                size="small"
                sx={{ flex: 1, whiteSpace: 'nowrap' }}
              >
                مسح الفلاتر
              </Button>
              {hasInvoicePagePermission(page?.sequentialId || '', 'import_all_excel') && (
                <Tooltip title="تصدير جميع الفواتير إلى Excel">
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={exportingExcel ? <CircularProgress size={16} color="inherit" /> : <TableChartIcon />}
                    onClick={handleExportAllToExcel}
                    disabled={exportingExcel}
                    size="small"
                    sx={{ flex: 1, whiteSpace: 'nowrap', bgcolor: '#217346', '&:hover': { bgcolor: '#1a5c38' } }}
                  >
                    {exportingExcel ? 'جاري...' : 'Excel'}
                  </Button>
                </Tooltip>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Invoices Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : invoices.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          لا توجد فواتير متاحة. يمكنك إنشاء فاتورة جديدة بالنقر على زر "إنشاء فاتورة جديدة".
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                <TableCell>رقم الفاتورة</TableCell>
                <TableCell>التاريخ</TableCell>
                <TableCell>العميل/المورد</TableCell>
                <TableCell>الفرع</TableCell>
                <TableCell>المستخدم</TableCell>
                <TableCell>المجموع</TableCell>
                {/* عرض عمود المدفوع/المتبقي فقط إذا كان الدفع الجزئي مفعلاً */}
                {page?.enablePartialPayment && (
                  <TableCell>المدفوع / المتبقي</TableCell>
                )}
                <TableCell>المرتجعات</TableCell>
                <TableCell>الحالة</TableCell>
                <TableCell>حالة الإرسال</TableCell>
                <TableCell align="center">حالة الأحداث</TableCell>
                <TableCell align="center">المعاملات المالية</TableCell>
                <TableCell>الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map(invoice => {
                  return (
                    <TableRow key={invoice._id} hover>
                      <TableCell>{invoice.invoiceNumber}</TableCell>
                      <TableCell>
                        {(() => {
                          // استخراج التاريخ فقط بدون تحويل المنطقة الزمنية
                          const dateStr = invoice.date.split('T')[0];
                          const [year, month, day] = dateStr.split('-');
                          return `${day}/${month}/${year}`;
                        })()}
                      </TableCell>
                      <TableCell>
                        {invoice.customerName || invoice.supplierName || '-'}
                      </TableCell>
                      <TableCell style={{background: invoice.branchName ? '#f0f9ff' : '#fff5f5'}}>
                        {invoice.branchName}
                      </TableCell>
                      <TableCell style={{background: '#f9f9f9'}}>
                        {invoice.createdByName || '-'}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(invoice.total)}
                      </TableCell>
                      {/* عرض المدفوع/المتبقي فقط إذا كان الدفع الجزئي مفعلاً */}
                      {page?.enablePartialPayment && (
                        <TableCell>
                          {loadingPaymentTotals || invoicePaymentTotals[invoice._id!] === undefined ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 40 }}>
                              <CircularProgress size={20} />
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                  المدفوع:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'success.main' }}>
                                  {formatCurrency(invoicePaymentTotals[invoice._id!])}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                  المتبقي:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'error.main' }}>
                                  {formatCurrency(invoice.total - invoicePaymentTotals[invoice._id!])}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        {(invoice as any).returnInvoices && (invoice as any).returnInvoices.length > 0 ? (
                          <Box>
                            {(invoice as any).returnInvoices.map((returnInv: any, index: number) => (
                              <Chip
                                key={index}
                                label={`${returnInv.invoiceNumber} (${formatCurrency(returnInv.returnAmount)})`}
                                size="small"
                                color="warning"
                                sx={{ mb: 0.5, mr: 0.5 }}
                                onClick={() => navigate(`/invoices/${pageId}/view/${returnInv.invoiceId}`)}
                                clickable
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invoice.status === 'completed' ? 'مكتملة' : 'مسودة'}
                          color={invoice.status === 'completed' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {/* عرض حالة الإرسال للهيئة */}
                        {(invoice as any).zatcaPhase2?.submissionStatus ? (
                          <Tooltip
                            title={
                              (invoice as any).zatcaPhase2?.submissionStatus === 'accepted'
                                ? `تم القبول - UUID: ${(invoice as any).zatcaPhase2?.uuid || 'N/A'}`
                                : (invoice as any).zatcaPhase2?.submissionStatus === 'rejected'
                                ? `مرفوضة - ${(invoice as any).zatcaPhase2?.validationErrors?.[0]?.message || 'خطأ غير معروف'}`
                                : (invoice as any).zatcaPhase2?.submissionStatus === 'submitted'
                                ? 'قيد المراجعة من الهيئة'
                                : 'لم يتم الإرسال بعد'
                            }
                          >
                            <Chip
                              icon={
                                (invoice as any).zatcaPhase2?.submissionStatus === 'accepted' ? (
                                  <CheckCircleIcon fontSize="small" />
                                ) : (invoice as any).zatcaPhase2?.submissionStatus === 'rejected' ? (
                                  <CancelIcon fontSize="small" />
                                ) : (invoice as any).zatcaPhase2?.submissionStatus === 'submitted' ? (
                                  <HourglassEmptyIcon fontSize="small" />
                                ) : (
                                  <RadioButtonUncheckedIcon fontSize="small" />
                                )
                              }
                              label={
                                (invoice as any).zatcaPhase2?.submissionStatus === 'accepted'
                                  ? 'مقبولة'
                                  : (invoice as any).zatcaPhase2?.submissionStatus === 'rejected'
                                  ? 'مرفوضة'
                                  : (invoice as any).zatcaPhase2?.submissionStatus === 'submitted'
                                  ? 'قيد المراجعة'
                                  : 'لم ترسل'
                              }
                              color={
                                (invoice as any).zatcaPhase2?.submissionStatus === 'accepted'
                                  ? 'success'
                                  : (invoice as any).zatcaPhase2?.submissionStatus === 'rejected'
                                  ? 'error'
                                  : (invoice as any).zatcaPhase2?.submissionStatus === 'submitted'
                                  ? 'warning'
                                  : 'default'
                              }
                              size="small"
                              sx={{ minWidth: 100 }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {/* عرض حالة الأحداث */}
                        <EventStatusIndicator
                          status={invoiceStatuses[invoice._id!]}
                          loading={loadingStatuses}
                          invoiceId={invoice._id!}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {/* عرض حالة المعاملات المالية */}
                        <TransactionStatusIndicator
                          transactionStatus={invoiceTransactions[invoice._id!]}
                          loading={loadingTransactions}
                          invoiceNumber={invoice.invoiceNumber}
                          invoice={invoice}
                        />
                      </TableCell>
                      <TableCell>
                        {/* عرض أزرار الإجراءات فقط إذا كان المستخدم يملك الصلاحيات المناسبة */}
                        {hasInvoicePagePermission(page?.sequentialId || '', 'edit') && (
                          <Tooltip title="تعديل">
                            <IconButton
                              size="small"
                              onClick={() => handleEditInvoice(invoice._id!)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasInvoicePagePermission(page?.sequentialId || '', 'delete') && (
                          <Tooltip title="حذف">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteInvoice(invoice._id!)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasInvoicePagePermission(page?.sequentialId || '', 'view') && (
                          <Tooltip title="مشاهدة">
                            <IconButton
                              size="small"
                              onClick={() => handleViewInvoice(invoice._id!)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasInvoicePagePermission(page?.sequentialId || '', 'print') && (
                          <Tooltip title="طباعة">
                            <IconButton
                              size="small"
                              onClick={() => handlePrintInvoice(invoice._id!)}
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasInvoicePagePermission(page?.sequentialId || '', 'print') && (
                          <Tooltip title="تصدير PDF">
                            <IconButton
                              size="small"
                              onClick={() => handleExportToPdf(invoice._id!)}
                            >
                              <FileDownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasInvoicePagePermission(page?.sequentialId || '', 'import_excel') && (
                          <Tooltip title="تصدير Excel">
                            <IconButton
                              size="small"
                              onClick={() => handleExportToExcel(invoice._id!)}
                            >
                              <ExportIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasInvoicePagePermission(page?.sequentialId || '', 'view_payment_management') && (
                          <Tooltip title="تسديد الفاتورة">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleOpenPaymentDialog(invoice)}
                            >
                              <PaymentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          display="flex"
          justifyContent="center"
          mt={3}
          mb={2}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Print Templates Dialog */}
      <Dialog open={printTemplatesDialog} onClose={() => setPrintTemplatesDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>اختر قالب الطباعة</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            اختر قالب الطباعة المناسب للفاتورة:
          </Typography>
          <Grid container spacing={2}>
            {printTemplates.map((template) => (
              <Grid item xs={12} key={template.id}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handlePrintWithTemplate(template.id)}
                >
                  {template.name}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintTemplatesDialog(false)} color="primary">
            إلغاء
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Payment Management Dialog */}
      {selectedInvoiceForPayment && page && (
        <InvoicePaymentManagementDialog
          open={paymentDialogOpen}
          onClose={handleClosePaymentDialog}
          invoiceId={selectedInvoiceForPayment._id!}
          invoiceNumber={selectedInvoiceForPayment.invoiceNumber}
          invoiceTotal={selectedInvoiceForPayment.total}
          cashAccountId={page.cashAccountId}
          cashPaidAmount={selectedInvoiceForPayment.cashPaidAmount || 0}
          paymentMethodsTotal={
            selectedInvoiceForPayment.paymentMethods && selectedInvoiceForPayment.paymentMethods.length > 0
              ? selectedInvoiceForPayment.paymentMethods.reduce((sum, pm) => sum + pm.amount, 0)
              : 0
          }
        />
      )}
    </Box>
  );
};

export default InvoicePage;